import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  MenuItem,
  CircularProgress,
  Chip,
  Alert,
  Typography,
  Divider,
  Switch,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import axios from 'axios';

type SubjectType = 'THEORY' | 'PRACTICAL' | 'BOTH';

interface ClassGroup {
  id: string;
  name: string;
}
interface ClassModel {
  id: string;
  name: string;
  isActive?: boolean;
}
interface GlobalSubjectMaster {
  id: string;
  name: string;
  code: string;
  subjectType: SubjectType;
}
interface SubjectModel {
  id: string;
  name: string;
  code?: string;
  subjectType: SubjectType;
  maxMarks: number;
  isActive: boolean;
  classId: string;
  subjectMasterId?: string;
  class?: ClassModel;
  subjectMaster?: GlobalSubjectMaster;
}

const SubjectMaster: React.FC = () => {
  const [subjects, setSubjects] = useState<SubjectModel[]>([]);
  const [classGroups, setClassGroups] = useState<ClassGroup[]>([]);
  const [classes, setClasses] = useState<ClassModel[]>([]);
  const [subjectMasters, setSubjectMasters] = useState<GlobalSubjectMaster[]>([]);
  
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [selectedClassId, setSelectedClassId] = useState<string>('');
  
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    subjectType: 'THEORY' as SubjectType,
    maxMarks: 100,
    classId: '',
    subjectMasterId: '',
    isActive: true,
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchClassGroups();
    fetchSubjectMasters();
  }, []);

  useEffect(() => {
    if (selectedGroupId) {
      fetchClasses(selectedGroupId);
    } else {
      setClasses([]);
      setSelectedClassId('');
    }
  }, [selectedGroupId]);

  useEffect(() => {
    if (selectedClassId) {
      fetchSubjects(selectedClassId);
    } else {
      setSubjects([]);
    }
  }, [selectedClassId]);

  const fetchClassGroups = async () => {
    try {
      const res = await axios.get('/system-settings/class-group?isActive=true');
      setClassGroups(res.data);
    } catch (err) {
      console.error('Failed to fetch class groups', err);
    }
  };

  const fetchSubjectMasters = async () => {
    try {
      const res = await axios.get('/system-settings/masters/subjectMaster?isActive=true');
      setSubjectMasters(res.data);
    } catch (err) {
      console.error('Failed to fetch global subjects', err);
    }
  };

  const fetchClasses = async (groupId: string) => {
    try {
      const res = await axios.get('/system-settings/classes', { params: { classGroupId: groupId } });
      setClasses(res.data.filter((c: any) => c.isActive));
    } catch (err) {
      console.error('Failed to fetch classes', err);
    }
  };

  const fetchSubjects = async (classId: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get('/system-settings/subject', { params: { classId } });
      setSubjects(res.data);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch subjects');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingId(null);
    setViewMode(false);
    setFormData({
      name: '',
      code: '',
      subjectType: 'THEORY',
      maxMarks: 100,
      classId: selectedClassId || '',
      subjectMasterId: '',
      isActive: true,
    });
    setDialogOpen(true);
  };

  const handleEdit = (sub: SubjectModel) => {
    setEditingId(sub.id);
    setViewMode(false);
    setFormData({
      name: sub.name,
      code: sub.code || '',
      subjectType: sub.subjectType,
      maxMarks: sub.maxMarks,
      classId: sub.classId,
      subjectMasterId: sub.subjectMasterId || '',
      isActive: sub.isActive,
    });
    setDialogOpen(true);
  };

  const handleView = (sub: SubjectModel) => {
    setEditingId(sub.id);
    setViewMode(true);
    setFormData({
      name: sub.name,
      code: sub.code || '',
      subjectType: sub.subjectType,
      maxMarks: sub.maxMarks,
      classId: sub.classId,
      subjectMasterId: sub.subjectMasterId || '',
      isActive: sub.isActive,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this subject?')) return;
    try {
      await axios.delete(`/system-settings/subject/${id}`);
      if (selectedClassId) fetchSubjects(selectedClassId);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete subject');
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      await axios.patch(`/system-settings/subject/${id}/toggle-status`);
      setSubjects((prev) =>
        prev.map((sub) =>
          sub.id === id ? { ...sub, isActive: !currentStatus } : sub
        )
      );
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to toggle status');
      if (selectedClassId) fetchSubjects(selectedClassId);
    }
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        maxMarks: Number(formData.maxMarks),
      };
      if (editingId && !viewMode) {
        await axios.patch(`/system-settings/subject/${editingId}`, payload);
      } else if (!editingId) {
        await axios.post('/system-settings/subject', payload);
      }
      setDialogOpen(false);
      if (selectedClassId) fetchSubjects(selectedClassId);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to save subject');
    }
  };

  const handleMasterSelect = (masterId: string) => {
    const master = subjectMasters.find(m => m.id === masterId);
    if (master) {
      setFormData({
        ...formData,
        subjectMasterId: masterId,
        name: master.name,
        code: master.code,
        subjectType: master.subjectType,
      });
    } else {
       // Clear if needed, or keep previous
       setFormData({
        ...formData,
        subjectMasterId: '',
      });
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Class-wise Subject Configuration</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            select
            label="Class Group"
            value={selectedGroupId}
            onChange={(e) => setSelectedGroupId(e.target.value)}
            sx={{ minWidth: 180 }}
            size="small"
          >
            <MenuItem value="">Select Group</MenuItem>
            {classGroups.map((group) => (
              <MenuItem key={group.id} value={group.id}>
                {group.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Class"
            value={selectedClassId}
            onChange={(e) => setSelectedClassId(e.target.value)}
            sx={{ minWidth: 180 }}
            size="small"
            disabled={!selectedGroupId}
          >
            <MenuItem value="">Select Class</MenuItem>
            {classes.map((cls) => (
              <MenuItem key={cls.id} value={cls.id}>
                {cls.name}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate} disabled={!selectedClassId}>
            Add Subject
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Subject Name</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Max Marks</TableCell>
              <TableCell>Global Master</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center"><CircularProgress /></TableCell>
              </TableRow>
            ) : subjects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {selectedClassId ? 'No subjects found.' : 'Please select a Class to view subjects.'}
                </TableCell>
              </TableRow>
            ) : (
              subjects.map((sub) => (
                <TableRow key={sub.id}>
                  <TableCell>{sub.name}</TableCell>
                  <TableCell>{sub.code || '-'}</TableCell>
                  <TableCell>{sub.subjectType}</TableCell>
                  <TableCell>{sub.maxMarks}</TableCell>
                  <TableCell>
                    {sub.subjectMaster ? (
                        <Chip label="Linked" size="small" color="primary" variant="outlined" />
                    ) : (
                        <Chip label="Custom" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={sub.isActive}
                      onChange={() => handleToggleStatus(sub.id, sub.isActive)}
                      color="primary"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleView(sub)}><VisibilityIcon /></IconButton>
                    <IconButton size="small" onClick={() => handleEdit(sub)}><EditIcon /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(sub.id)}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{viewMode ? 'View Subject' : editingId ? 'Edit Subject' : 'New Subject'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            
            {!viewMode && (
                <Box sx={{ p: 2, border: '1px dashed grey', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>Select Global Subject (Recommended)</Typography>
                    <TextField
                        select
                        label="Global Subject Master"
                        value={formData.subjectMasterId}
                        onChange={(e) => handleMasterSelect(e.target.value)}
                        fullWidth
                        size="small"
                    >
                        <MenuItem value="">-- Custom / None --</MenuItem>
                        {subjectMasters.map((m) => (
                            <MenuItem key={m.id} value={m.id}>
                                {m.name} ({m.code})
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>
            )}

            <Divider />
            
            <TextField
              select
              label="Class"
              value={formData.classId}
              onChange={(e) => setFormData({ ...formData, classId: e.target.value })}
              fullWidth
              required
              disabled
            >
              {classes.map((cls) => (
                <MenuItem key={cls.id} value={cls.id}>
                  {cls.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Subject Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              disabled={viewMode || !!formData.subjectMasterId}
              helperText={formData.subjectMasterId ? "Inherited from Global Master" : "Enter name manually"}
            />
            <TextField
              label="Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              fullWidth
              disabled={viewMode || !!formData.subjectMasterId}
              helperText={formData.subjectMasterId ? "Inherited from Global Master" : ""}
            />
            <TextField
              select
              label="Subject Type"
              value={formData.subjectType}
              onChange={(e) => setFormData({ ...formData, subjectType: e.target.value as SubjectType })}
              fullWidth
              required
              disabled={viewMode || !!formData.subjectMasterId}
            >
              <MenuItem value="THEORY">Theory</MenuItem>
              <MenuItem value="PRACTICAL">Practical</MenuItem>
              <MenuItem value="BOTH">Both</MenuItem>
            </TextField>
            <TextField
              label="Max Marks"
              type="number"
              value={formData.maxMarks}
              onChange={(e) => setFormData({ ...formData, maxMarks: Number(e.target.value) })}
              fullWidth
              required
              disabled={viewMode}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  disabled={viewMode}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
          {!viewMode && <Button onClick={handleSave} variant="contained">Save</Button>}
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default SubjectMaster;
